import { createContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import {
  fetchPledgeStakes as fetchPledgeStakesAction,
  FetchPledgeStakesPayload,
} from '../actions/paymentOptions';
import { selectBetIds, selectBets } from '../selectors/entries';
import { PledgeStakesDAO } from '../types/backend/paymentOptions';
import { EntriesBets } from '../types/entries';

// ---

type PaymentOptionState = {
  items: PledgeStakesDAO;
  loading: boolean;
  requested: boolean;
};

const PAYMENT_OPTION_STATE = {
  items: {
    singleStakes: [],
    chainStakes: [],
  },
  loading: false,
  requested: false,
};

// ---

type PledgeStakesContextData = {
  canMakeRequest: boolean;
  pledgeStakes: PaymentOptionState;
  fetchPledgeStakes: (betId?: number) => void;
};

const pledgeStakesContextInitialValue: PledgeStakesContextData = {
  canMakeRequest: false,
  pledgeStakes: PAYMENT_OPTION_STATE,
  fetchPledgeStakes: () => undefined,
};

// ---

export const PledgeStakesContext = createContext<
  PledgeStakesContextData
>(pledgeStakesContextInitialValue);

export const usePledgeStakesContextValue = (): PledgeStakesContextData => {
  let isMounted = true;
  const controller = new AbortController();

  const isLoggedIn = useSelector(selectIsLoggedIn);
  const betIds = useSelector(selectBetIds);
  const bets = useSelector(selectBets);

  const [pledgeStakes, setPledgeStakes] = useState<PaymentOptionState>(PAYMENT_OPTION_STATE);

  const [canMakeRequest, setCanMakeRequest] = useState(false);

  const fetchPledgeStakes = (betId?: number) => {
    setPledgeStakes({ ...PAYMENT_OPTION_STATE, loading: true });
    setCanMakeRequest(false);

    const payload = createFetchPledgeStakePayload(betId, bets);

    fetchPledgeStakesAction(controller, payload)
      .then(({ data }) => {
        if (isMounted) {
          const sortedItems = data.singleStakes
            .map((stake) => ({
              ...stake,
              matchTime: new Date(stake.createdAt).getTime(),
            }))
            .sort((a, b) => b.matchTime - a.matchTime);

          const items = { singleStakes: sortedItems, chainStakes: data.chainStakes };

          setPledgeStakes({ loading: false, requested: true, items });
        }
      })
      .catch((error) => {
        if (isMounted) {
          console.error('usePledgeStakesContextValue -> fetchPledgeStakes error:', error);

          setPledgeStakes({ ...PAYMENT_OPTION_STATE, requested: true });
          setCanMakeRequest(true);
        }
      });
  };

  useEffect(() => () => {
    isMounted = false;
    controller.abort();
  }, []);

  useMemo(() => {
    setCanMakeRequest(true);
    return;
  }, [betIds, isLoggedIn]);

  return {
    canMakeRequest,
    pledgeStakes,
    fetchPledgeStakes,
  };
};

// ---

export function createFetchPledgeStakePayload(
  betId: number | undefined,
  bets: EntriesBets,
): FetchPledgeStakesPayload | undefined {
  let payload: FetchPledgeStakesPayload | undefined;

  if (betId) {
    payload = {
      bets: bets.reduce((acc: FetchPledgeStakesPayload['bets'], { entity, price }) => {
        if (betId === entity.id) {
          acc.push({ id: entity.id, matchId: entity.matchId, price });
        }

        return acc;
      }, []),
    };
  }

  return payload;
}
